import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import { Badge, Button, Card, CardContent, DropdownSelect, EmptyState, ErrorState, Input, PageHeader, SkeletonRow, SkeletonText, Textarea, useConfirm } from '@/components/ui';

/**
 * Admin > Roles: list and full-page editor.
 *
 * Modelled on the captured Freshdesk screens (FRESHDESK_PARITY_GAP_PLAN.md §27):
 *   image7f.png / image80.png - roles list with per-role agent counts
 *   image81.png               - full-page "Edit Role": Cancel/Save in the header,
 *                               left-label form rows, then a Permissions block with
 *                               category quick-nav and nested capability checkboxes.
 *
 * Deviation from Freshdesk, per product owner: built-in roles remain editable and
 * deactivatable here (Freshdesk locks them). They still cannot be deleted.
 */

export type Role = {
  id: string;
  name: string;
  type: string;
  description?: string;
  permissions: Record<string, boolean>;
  isBuiltIn: boolean;
  isActive: boolean;
  _count?: { users: number };
};

type Capability = {
  key: string;
  label: string;
  description?: string;
  enforced: boolean;
  children?: Capability[];
};

type Category = {
  key: string;
  label: string;
  description: string;
  capabilities: Capability[];
};

const ROLE_TYPES = ['super_admin', 'administrator', 'supervisor', 'agent', 'collaborator', 'customer'];

const problem = (error: unknown, fallback: string) =>
  error instanceof ApiError ? error.message : fallback;

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  if (!message) return null;
  return <ErrorState message={message} onRetry={onRetry} />;
}

/** Shared admin page chrome: breadcrumb, title, subtitle, right-aligned actions. */
function AdminHeader({
  breadcrumb,
  title,
  subtitle,
  actions,
}: {
  breadcrumb: string;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  const { t } = useI18n();
  return (
    <PageHeader
      className="mb-0"
      breadcrumb={[{ label: t('nav.settings'), to: '/settings' }, { label: breadcrumb }]}
      title={title}
      description={subtitle}
      actions={actions}
    />
  );
}

/** Right-hand explanatory column that every Freshdesk admin screen carries. */
function HelpPanel({ children }: { children: React.ReactNode }) {
  return (
    <aside className="space-y-4 text-sm leading-relaxed text-ink-muted lg:border-l lg:border-line lg:pl-6">
      {children}
    </aside>
  );
}

export function RolesListPage() {
  const { t, tOr } = useI18n();
  const { confirm } = useConfirm();
  const [roles, setRoles] = React.useState<Role[]>([]);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setError('');
      setRoles(await api.get<Role[]>('/admin/roles'));
    } catch (cause) {
      setError(problem(cause, t('common.requestFailed')));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function remove(role: Role) {
    if (!await confirm({ title: t('adm.groups.confirmTitle', { name: role.name }), description: t('adm.roles.confirmDesc'), confirmLabel: t('adm.roles.confirmLabel') })) return;
    try {
      await api.delete(`/admin/roles/${role.id}`);
      await load();
    } catch (cause) {
      setError(problem(cause, t('common.requestFailed')));
    }
  }

  return (
    <div className="animate-fade-in space-y-6">
      <AdminHeader
        breadcrumb={t('adm.roles.title')}
        title={t('adm.roles.heading')}
        subtitle={t('adm.roles.subtitle')}
        actions={
          <Link
            to="/settings/roles/new"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-brand-600 px-4 text-sm font-medium text-white shadow-card transition-all duration-150 hover:bg-brand-700 hover:shadow-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            {t('adm.roles.newRole')}
          </Link>
        }
      />
      <ErrorBanner message={error} onRetry={() => void load()} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Card className="overflow-hidden">
          <CardContent className="divide-y divide-line p-0">
            {loading && <SkeletonRow rows={4} />}
            {!loading && roles.length === 0 && (
              <EmptyState
                title={t('adm.roles.createFirst')}
                description={t('adm.roles.createFirstDesc')}
              />
            )}
            {roles.map((role) => (
              <div
                key={role.id}
                className="group flex flex-wrap items-start justify-between gap-4 p-5 transition-colors duration-150 hover:bg-brand-50/50"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/settings/roles/${role.id}`}
                      className="font-semibold text-brand-700 transition-colors hover:text-brand-800 hover:underline"
                    >
                      {role.name}
                    </Link>
                    {role.isBuiltIn && (
                      <span
                        className="text-xs text-ink-subtle"
                        title={t('adm.roles.builtInTitle')}
                      >
                        {t('adm.roles.builtIn')}
                      </span>
                    )}
                    {!role.isActive && <Badge variant="neutral">{t('adm.roles.inactive')}</Badge>}
                  </div>
                  {role.description && (
                    <p className="mt-1 max-w-2xl text-sm text-ink-muted">{role.description}</p>
                  )}
                  <p className="mt-1 text-xs text-ink-subtle">{tOr(`adm.roleType.${role.type}`, role.type)}</p>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-sm text-ink-muted">
                    {role._count?.users ? t('adm.roles.agentsCount', { count: role._count.users }) : t('adm.roles.noAgent')}
                  </span>
                  <div className="flex gap-2 opacity-80 transition-opacity duration-150 group-hover:opacity-100">
                    <Link
                      to={`/settings/roles/${role.id}`}
                      className="inline-flex h-8 items-center justify-center rounded-lg border border-line-strong bg-surface px-3 text-xs font-medium text-ink shadow-card transition-all duration-150 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                    >
                      {t('adm.edit')}
                    </Link>
                    {!role.isBuiltIn && !(role._count?.users ?? 0) && (
                      <Button size="sm" variant="destructive" onClick={() => void remove(role)}>
                        {t('common.delete')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <HelpPanel>
          <h2 className="text-base font-semibold text-ink">{t('adm.roles.understandingTitle')}</h2>
          <p>{t('adm.roles.understandingBody1')}</p>
          <p>{t('adm.roles.understandingBody2')}</p>
          <h2 className="text-base font-semibold text-ink">{t('adm.roles.scopeTitle')}</h2>
          <p>{t('adm.roles.scopeBody')}</p>
        </HelpPanel>
      </div>
    </div>
  );
}

export function RoleEditPage() {
  const { t, tOr } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [categories, setCategories] = React.useState<Category[]>([]);
  const [role, setRole] = React.useState<Role | null>(null);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [type, setType] = React.useState('agent');
  const [isActive, setActive] = React.useState(true);
  const [granted, setGranted] = React.useState<Record<string, boolean>>({});
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        setError('');
        const catalogue = await api.get<Category[]>('/admin/roles/permission-catalogue');
        if (cancelled) return;
        setCategories(catalogue);

        if (!isNew) {
          const existing = await api.get<Role>(`/admin/roles/${id}`);
          if (cancelled) return;
          setRole(existing);
          setName(existing.name);
          setDescription(existing.description ?? '');
          setType(existing.type);
          setActive(existing.isActive);
          setGranted(existing.permissions ?? {});
        }
      } catch (cause) {
        if (!cancelled) setError(problem(cause, t('common.requestFailed')));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  /** Unticking a parent also clears its children, which are meaningless alone. */
  function toggle(capability: Capability, checked: boolean) {
    setSaved(false);
    setGranted((current) => {
      const next = { ...current, [capability.key]: checked };
      if (!checked) {
        for (const child of capability.children ?? []) delete next[child.key];
      }
      return next;
    });
  }

  const [saved, setSaved] = React.useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      setError('');
      setSaved(false);
      const catalogueKeys = categories.flatMap((category) => (
        category.capabilities.flatMap(function collect(capability): string[] {
          return capability.children
            ? [capability.key, ...capability.children.flatMap(collect)]
            : [capability.key];
        })
      ));
      const permissions = Object.fromEntries(
        catalogueKeys.map((key) => [key, granted[key] === true]),
      );
      if (isNew) {
        const created = await api.post<Role>('/admin/roles', {
          name,
          type,
          description: description || undefined,
          permissions,
          isActive,
        });
        // Freshdesk's Edit Role stays on the page after Save (image81.png); route to
        // the persisted record instead of leaving the editor, so the admin can see the
        // save actually took effect rather than being bounced back to the list.
        navigate(`/settings/roles/${created.id}`, { replace: true });
        setSaved(true);
      } else {
        const updated = await api.put<Role>(`/admin/roles/${id}`, {
          name,
          description: description || undefined,
          permissions,
          isActive,
        });
        setRole(updated);
        setGranted(updated.permissions ?? {});
        setSaved(true);
      }
    } catch (cause) {
      setError(problem(cause, t('common.requestFailed')));
    } finally {
      setSaving(false);
    }
  }

  const grantedCount = Object.values(granted).filter(Boolean).length;

  return (
    <form className="animate-fade-in space-y-6" onSubmit={save}>
      <AdminHeader
        breadcrumb={t('adm.roles.title')}
        title={isNew ? t('adm.roles.newRole') : t('adm.roles.editRole')}
        subtitle={
          role?.isBuiltIn
            ? t('adm.roles.builtInNote')
            : undefined
        }
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => navigate('/settings/roles')}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving || loading}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </>
        }
      />
      <ErrorBanner message={error} />
      {saved && (
        <div
          role="status"
          className="animate-fade-in rounded-xl border border-success-500/20 bg-success-50 px-4 py-3 text-sm text-success-700"
        >
          {t('adm.roles.saved')}
        </div>
      )}

      <Card>
        <CardContent className="space-y-5 p-6">
          <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
            <label htmlFor="role-name" className="text-sm font-semibold text-ink">
              {t('common.name')} <span className="text-accent-500">*</span>
            </label>
            <Input
              id="role-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)]">
            <label htmlFor="role-description" className="pt-2 text-sm font-semibold text-ink">
              {t('adm.description')}
            </label>
            <Textarea
              id="role-description"
              rows={4}
              placeholder={t('adm.description')}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>

          <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start">
            <label htmlFor="role-type" className="pt-2 text-sm font-semibold text-ink">
              {t('adm.roles.agentType')}
            </label>
            <div>
              <DropdownSelect
                options={ROLE_TYPES.map((value) => ({ value, label: tOr(`adm.roleType.${value}`, value) }))}
                value={type}
                disabled={!isNew}
                onChange={(value) => setType(value)}
                searchable={false}
              />
              <p className="mt-1.5 text-xs text-ink-subtle">
                {t('adm.roles.typeLocked')}
              </p>
            </div>
          </div>

          {!isNew && (
            <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start">
              <span className="pt-0.5 text-sm font-semibold text-ink">{t('adm.roles.agentsLabel')}</span>
              <div>
                <Link
                  to="/settings/agents"
                  className="text-sm font-medium text-brand-700 transition-colors hover:text-brand-800 hover:underline"
                >
                  {t('adm.roles.agentsManage', { count: role?._count?.users ?? 0 })}
                </Link>
                <p className="mt-1.5 text-xs text-ink-subtle">
                  {t('adm.roles.agentsAssignedNote')}
                </p>
              </div>
            </div>
          )}

          <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
            <span className="text-sm font-semibold text-ink">{t('adm.roles.status')}</span>
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-line-strong text-brand-600 transition focus:ring-2 focus:ring-brand-500"
                checked={isActive}
                onChange={(event) => setActive(event.target.checked)}
              />
              {t('adm.roles.activeHelp')}
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-5 p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line pb-3">
            <h2 className="text-lg font-semibold text-ink">{t('adm.roles.permissions')}</h2>
            <span className="text-xs text-ink-subtle">{t('adm.roles.granted', { count: grantedCount })}</span>
          </div>

          {loading && <SkeletonText lines={6} />}

          {!loading && categories.length > 0 && (
            <nav
              aria-label={t('adm.roles.categoriesAria')}
              className="flex flex-wrap items-center gap-1 text-sm"
            >
              <span className="mr-1 text-ink-subtle">{t('adm.roles.scrollTo')}</span>
              {categories.map((category) => (
                <a
                  key={category.key}
                  href={`#permissions-${category.key}`}
                  className="rounded-md px-2 py-1 font-medium text-brand-700 transition-colors hover:bg-brand-50 hover:text-brand-800"
                >
                  {tOr(`adm.perm.cat.${category.key}.label`, category.label)}
                </a>
              ))}
            </nav>
          )}

          {categories.map((category) => (
            <section
              key={category.key}
              id={`permissions-${category.key}`}
              className="grid scroll-mt-6 gap-3 border-t border-line pt-5 md:grid-cols-[12rem_minmax(0,1fr)]"
            >
              <div>
                <h3 className="text-base font-semibold text-ink">{tOr(`adm.perm.cat.${category.key}.label`, category.label)}</h3>
                <p className="mt-1 text-xs text-ink-subtle">{tOr(`adm.perm.cat.${category.key}.desc`, category.description)}</p>
              </div>
              <div>
                <p className="mb-2 text-sm font-semibold text-ink">{t('adm.roles.agentCan')}</p>
                <ul className="space-y-2">
                  {category.capabilities.map((capability) => (
                    <CapabilityRow
                      key={capability.key}
                      capability={capability}
                      granted={granted}
                      onToggle={toggle}
                    />
                  ))}
                </ul>
              </div>
            </section>
          ))}
        </CardContent>
      </Card>
    </form>
  );
}

function CapabilityRow({
  capability,
  granted,
  onToggle,
  depth = 0,
}: {
  capability: Capability;
  granted: Record<string, boolean>;
  onToggle: (capability: Capability, checked: boolean) => void;
  depth?: number;
}) {
  const { t, tOr } = useI18n();
  const checked = granted[capability.key] === true;
  return (
    <li style={{ marginLeft: depth * 20 }}>
      <label className="inline-flex cursor-pointer items-start gap-2 text-sm text-ink">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-line-strong text-brand-600 transition focus:ring-2 focus:ring-brand-500"
          checked={checked}
          onChange={(event) => onToggle(capability, event.target.checked)}
        />
        <span>
          {tOr(`adm.perm.cap.${capability.key}.label`, capability.label)}
          {!capability.enforced && (
            <span
              className="ml-2 align-middle text-[11px] text-ink-subtle"
              title={t('adm.roles.notEnforcedTitle')}
            >
              {t('adm.roles.notEnforced')}
            </span>
          )}
          {capability.description && (
            <span className="block text-xs text-ink-subtle">{tOr(`adm.perm.cap.${capability.key}.desc`, capability.description)}</span>
          )}
        </span>
      </label>
      {capability.children && (
        <ul className="mt-2 space-y-2">
          {capability.children.map((child) => (
            <CapabilityRow
              key={child.key}
              capability={child}
              granted={granted}
              onToggle={onToggle}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
